import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  Box,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { trendingCollections } from '../data/collections';
import { getDailyRandomChange } from '../utils/priceUtils';
import VerificationBadge from './VerificationBadge';

function CollectionList({ activeTab }) {
  const navigate = useNavigate();
  const [collectionsWithChanges, setCollectionsWithChanges] = useState([]);
  
  useEffect(() => {
    // Update collections with current day's changes
    const updatedCollections = trendingCollections.map(collection => ({
      ...collection,
      currentPrice: collection.floorPrice, // Use static floor price
      volumeChange: getDailyRandomChange(collection.id)
    }));
    setCollectionsWithChanges(updatedCollections);
    
    // Update every hour
    const interval = setInterval(() => {
      const updatedCollections = trendingCollections.map(collection => ({
        ...collection,
        currentPrice: collection.floorPrice, // Use static floor price
        volumeChange: getDailyRandomChange(collection.id)
      }));
      setCollectionsWithChanges(updatedCollections);
    }, 3600000); // 1 hour in milliseconds
    
    return () => clearInterval(interval);
  }, []);

  const collections = activeTab === 1 
    ? collectionsWithChanges.slice(0, 20) 
    : collectionsWithChanges;

  return (
    <Paper 
      sx={{ 
        backgroundColor: 'rgba(15, 22, 36, 0.9)', 
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid rgba(0, 245, 255, 0.2)', 
        boxShadow: '0 4px 20px rgba(0, 245, 255, 0.1)' 
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ 
              '& th': { 
                borderBottom: '1px solid rgba(0, 245, 255, 0.2)', 
                color: '#8F9BB7', 
                fontWeight: 500,
                fontSize: '0.875rem',
                py: 2
              }
            }}>
              <TableCell>COLLECTION</TableCell>
              <TableCell align="right">FLOOR PRICE</TableCell>
              <TableCell align="right">24H VOLUME</TableCell>
              <TableCell align="right">24H CHANGE</TableCell>
              <TableCell align="right">TOTAL VOLUME</TableCell>
              <TableCell align="right">OWNERS</TableCell>
              <TableCell align="right">SUPPLY</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {collections.map((collection) => (
              <TableRow
                key={collection.id}
                hover
                onClick={() => navigate(`/collection/${collection.id}`)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out', 
                  '&:hover': { 
                    backgroundColor: 'rgba(0, 245, 255, 0.05)', 
                    '& .collection-name': {
                      textShadow: '0 0 10px rgba(0, 245, 255, 0.5)' 
                    }
                  },
                  '& td': { 
                    borderBottom: '1px solid rgba(0, 245, 255, 0.1)', 
                    color: 'white',
                    py: 2
                  }
                }}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={collection.image}
                      alt={collection.name}
                      sx={{ 
                        width: 40, 
                        height: 40,
                        border: '1px solid rgba(0, 245, 255, 0.2)',
                      }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        className="collection-name"
                        sx={{ 
                          fontWeight: 600,
                          color: 'white',
                          transition: 'all 0.3s ease-in-out'
                        }}
                      >
                        {collection.name}
                      </Typography>
                      <VerificationBadge size="small" />
                    </Stack>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 600 }}>
                    {collection.currentPrice.toFixed(3)} ETH
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>
                    {collection.volume24h.toFixed(1)} ETH
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${collection.volumeChange > 0 ? '+' : ''}${collection.volumeChange.toFixed(2)}%`}
                    size="small"
                    sx={{
                      backgroundColor: collection.volumeChange >= 0 ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                      color: collection.volumeChange >= 0 ? '#00FF00' : '#FF4444',
                      fontWeight: 600,
                      minWidth: 80,
                      '& .MuiChip-label': {
                        px: 2
                      }
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography>
                    {collection.totalVolume.toFixed(2)} ETH
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>
                    {collection.owners.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography>
                    {collection.supply.toLocaleString()}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default CollectionList;
